import { useContext, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button as GrommetButton, ButtonProps as GrommetButtonProps, ResponsiveContext } from 'grommet';
import { Box, BoxProps, Text } from '/src/components';

export const Button: React.FC<ButtonProps> = (props) => {
  const {
    label,
    targetUrl,
    onClick,
    color,
    icon,
    disabled,
    reverse,
    tip,
    toUpperCase = true,
    'aria-expanded': ariaExpanded,
    ...boxProps
  } = props;
  const navigate = useNavigate();
  const screenSize = useContext(ResponsiveContext);

  const [isHover, setIsHover] = useState(false);

  const isMobile = useMemo(() => screenSize === 'small' || screenSize === 'xsmall', [screenSize]);

  return (
    <Box
      height={isMobile ? undefined : 'xxlarge'}
      alignSelf="center"
      onMouseOver={() => setIsHover(true)}
      onMouseOut={() => setIsHover(false)}
      style={{ boxShadow: isHover ? 'rgba(0, 0, 0, 0.2) 0 10px 10px -10px' : '' }}
      transitionProperty="background, color, box-shadow, padding-bottom"
      transitionDuration="0.15s, 0.15s, 0.15s, 0.15s"
      transitionTimingFunction="ease, ease, ease, ease"
      background="white"
      direction="row"
      pad={{ horizontal: '0.75rem', vertical: isMobile ? '0.5rem' : undefined, bottom: isHover ? '1px' : undefined }}
      gap="xsmall"
      border={{ color: 'light-5', size: '0.8px' }}
      borderRadius="6px"
      {...boxProps}
    >
      <GrommetButton
        plain
        disabled={disabled}
        tip={tip}
        fill
        aria-expanded={ariaExpanded}
        onClick={() => (onClick ? onClick() : navigate(targetUrl ?? ''))}
      >
        <Box direction="row" gap="xsmall" justify="center">
          {icon && !reverse && <Box justify="center">{icon}</Box>}
          {label && (
            <Text
              textAlign="center"
              alignSelf="center"
              size={isMobile ? 'small' : 'medium'}
              lineHeight={isMobile ? '1rem' : undefined}
              color={color}
              toUpperCase={toUpperCase}
            >
              {label}
            </Text>
          )}
          {icon && reverse && <Box justify="center">{icon}</Box>}
        </Box>
      </GrommetButton>
    </Box>
  );
};

export type ButtonProps = BoxProps & {
  label?: string;
  targetUrl?: string;
  onClick?: () => void;
  color?: string;
  icon?: React.ReactNode;
  disabled?: boolean;
  reverse?: boolean;
  toUpperCase?: boolean;
  tip?: GrommetButtonProps['tip'];
  'aria-expanded'?: boolean;
};
